import { useContext, useState } from 'react'
import { TraitValue } from 'types'
import { panelPadding } from '../../../utils/globals'
import GlassPanel from '../../GlassPanel'
import { ExplorerConfigContext, ISessionContext } from '../../providers'
import { SDialog, SGrid, SIcon, SKitIcon, SMarkdown, SText, SView } from '../../scaled'
import { TraitSelectorProps } from './'
import SelectionHighlighter from './SelectionHighlighter'

export default function TraitGridSelector({
  group,
  onSelect,
  selections,
  ...rest
}: TraitSelectorProps) {
  const { learnMore, values } = group
  const { columns, rows } = gridCounts(values.length)
  const { fontSize, iconSize } = getSizes(values.length)

  const totalHeight: `${number}%` = `${5 * rows + 60}%`

  return (
    <SGrid
      pt={8}
      flex={1}
      pb={panelPadding}
      style={{ height: totalHeight }}
      columns={columns}
      gap={8}
    >
      {values.map((o, i) => (
        <ButtonItem
          key={i}
          option={o}
          fontSize={o.size || fontSize}
          iconSize={iconSize}
          radius={fontSize > 12 ? 32 : 12}
          helpPosition={learnMore ? (fontSize > 12 ? 10 : 5) : undefined}
          onSelect={() => onSelect(o)}
          isSelected={selections.some(s =>
            s.traitId ? s.traitId === o.id : s.title === rest.getLanguageValue(o.title)
          )}
          {...rest}
        />
      ))}
    </SGrid>
  )
}

interface ButtonItemProps {
  getLanguageValue: ISessionContext['getLanguageValue']
  onSelect: () => void
  option: TraitValue
  radius: number
  helpPosition?: number
  isSelected: boolean
  iconSize: number
  fontSize: number
}

function ButtonItem({
  fontSize,
  getLanguageValue,
  helpPosition,
  iconSize,
  isSelected,
  onSelect,
  option,
  radius = 12,
}: ButtonItemProps) {
  const { theme } = useContext(ExplorerConfigContext)
  const { icon, title, description } = option
  const [openDescription, setOpenDescription] = useState(false)

  return (
    <GlassPanel
      cursor="pointer"
      onClick={onSelect}
      flex={1}
      borderRadius={radius}
      borderWidth={2}
      key={getLanguageValue(title)}
      px={8}
      py={8}
      justify="center"
      align="center"
      overflow="hidden"
      {...(isSelected && { borderColor: theme.color.primary, backgroundColor: 'transparent' })}
    >
      <SelectionHighlighter isSelected={isSelected} />

      <SView justifyContent="center" alignSelf="center">
        {icon && <SKitIcon fontSize={iconSize} type={icon} />}
      </SView>

      <SView style={{ height: '10%', flexShrink: 1 }} />

      <SView zIndex={1} justify="center" minH={fontSize * 2.1}>
        <SText
          fontSize={fontSize}
          fontWeight="semibold"
          style={{ textAlign: 'center' }}
          // fitToWidth="100%"
        >
          {getLanguageValue(title)}
        </SText>
      </SView>

      {!!helpPosition && (
        <>
          <SIcon
            onClick={e => {
              e.stopPropagation()
              setOpenDescription(true)
            }}
            fontSize={20}
            position="absolute"
            top={helpPosition}
            right={helpPosition}
            value="info"
            type="outline"
          />
          <SDialog
            w={800}
            p={50}
            open={openDescription}
            onClose={() => setOpenDescription(false)}
            title={getLanguageValue(title)}
          >
            <SMarkdown noScroll value={getLanguageValue(description)} />
          </SDialog>
        </>
      )}
    </GlassPanel>
  )
}

function gridCounts(optionCount: number) {
  if (optionCount <= 4) return { columns: optionCount, rows: 1 }
  if (optionCount <= 6) return { columns: 3, rows: 2 }
  if (optionCount <= 8) return { columns: 4, rows: 2 }
  if (optionCount <= 12) return { columns: 4, rows: 3 }
  return { columns: 5, rows: 3 }
}

function getSizes(optionCount: number) {
  if (optionCount <= 4) return { fontSize: -2 * optionCount + 28, iconSize: 64 }
  if (optionCount <= 6) return { fontSize: 22, iconSize: 48 }
  if (optionCount <= 8) return { fontSize: 19, iconSize: 40 }
  if (optionCount <= 12) return { fontSize: 18, iconSize: 32 }
  return { fontSize: 16, iconSize: 28 }
}
